import type { PortalGrantRow, PortalWorkspaceRow } from "@/types/portalUser";

export type PortalContextValue = {
  email: string;
  /** All grants for the signed-in user (RLS). */
  grants: PortalGrantRow[];
  /**
   * Distinct branch/batch/association workspaces for News & Projects scoped parent.
   * Same shape as legacy "assignments" for active-workspace picker.
   */
  assignments: PortalWorkspaceRow[];
  activeAssignmentId: string | null;
  setActiveAssignmentId: (id: string | null) => void;
};

const BRANCH_CAPS = new Set(["branch_profile", "branch_news", "branch_projects"]);
const BATCH_CAPS = new Set(["batch_profile", "batch_news", "batch_projects"]);
const ASSOC_CAPS = new Set(["association_profile", "association_news", "association_projects"]);

/** Distinct workspaces (entity keys) from scoped grants. */
export function workspacesFromGrants(grants: PortalGrantRow[]): PortalWorkspaceRow[] {
  const map = new Map<string, PortalWorkspaceRow>();

  function upsert(ws: PortalWorkspaceRow) {
    const prev = map.get(ws.id);
    if (!prev || ws.created_at.localeCompare(prev.created_at) < 0) map.set(ws.id, ws);
  }

  for (const g of grants) {
    if (g.branch_id && BRANCH_CAPS.has(g.capability)) {
      upsert({
        id: `branch:${g.branch_id}`,
        kind: "branch",
        parent_id: g.branch_id,
        created_at: g.created_at,
      });
    }
    if (g.batch_id && BATCH_CAPS.has(g.capability)) {
      upsert({
        id: `batch:${g.batch_id}`,
        kind: "batch",
        parent_id: g.batch_id,
        created_at: g.created_at,
      });
    }
    if (g.association_id && ASSOC_CAPS.has(g.capability)) {
      upsert({
        id: `association:${g.association_id}`,
        kind: "association",
        parent_id: g.association_id,
        created_at: g.created_at,
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/**
 * True for `site_admin` or `manage_users` (portal user managers).
 * Both can edit any branch, batch, or association in the admin UI and via RLS.
 */
export function portalHasSiteWideWrite(grants: PortalGrantRow[]): boolean {
  return grants.some((g) => g.capability === "site_admin" || g.capability === "manage_users");
}

/** `manage_users` — can open Admin → Users and has the same org-wide entity access as site admins. */
export function portalIsSuperUser(grants: PortalGrantRow[]): boolean {
  return grants.some((g) => g.capability === "manage_users");
}

export function scopedPortalAssignments(workspaces: PortalWorkspaceRow[]): PortalWorkspaceRow[] {
  return workspaces;
}

export function pickActiveScopedAssignment(
  workspaces: PortalWorkspaceRow[],
  activeId: string | null
): PortalWorkspaceRow | null {
  if (workspaces.length === 0) return null;
  if (activeId) {
    const hit = workspaces.find((w) => w.id === activeId);
    if (hit) return hit;
  }
  return workspaces.sort((a, b) => a.created_at.localeCompare(b.created_at))[0] ?? null;
}

export function lockedScopeFromAssignment(
  a: PortalWorkspaceRow | null
): { lockedKind: "branch" | "batch" | "association"; lockedParentId: string } | null {
  if (!a) return null;
  return { lockedKind: a.kind, lockedParentId: a.parent_id };
}

export function tabIdsForGrants(grants: PortalGrantRow[]): string[] {
  if (grants.some((g) => g.capability === "manage_users")) {
    return ["overview", "news", "projects", "branches", "batches", "associations", "users"];
  }
  if (grants.some((g) => g.capability === "site_admin")) {
    return ["overview", "news", "projects", "branches", "batches", "associations"];
  }
  const ids: string[] = ["overview", "news", "projects"];
  if (grants.some((g) => BRANCH_CAPS.has(g.capability))) ids.push("branches");
  if (grants.some((g) => BATCH_CAPS.has(g.capability))) ids.push("batches");
  if (grants.some((g) => ASSOC_CAPS.has(g.capability))) ids.push("associations");
  return ids;
}

export function assignmentSummaryLabel(a: PortalWorkspaceRow): string {
  const shortId = (id: string) => `${id.slice(0, 8)}…`;
  if (a.kind === "branch") return `Branch · ${shortId(a.parent_id)}`;
  if (a.kind === "batch") return `Batch · ${shortId(a.parent_id)}`;
  return `Association · ${shortId(a.parent_id)}`;
}

export function grantsSummaryLabels(grants: PortalGrantRow[]): string[] {
  const labels: Record<string, string> = {
    manage_users: "Portal manager",
    site_admin: "Site-wide content admin",
    branch_profile: "Branch profile",
    branch_news: "Branch news",
    branch_projects: "Branch projects",
    batch_profile: "Batch profile",
    batch_news: "Batch news",
    batch_projects: "Batch projects",
    association_profile: "Association profile",
    association_news: "Association news",
    association_projects: "Association projects",
  };
  return Array.from(new Set(grants.map((g) => labels[g.capability] ?? g.capability)));
}
